import React, { useState } from 'react';
import { Input, CustomButton } from '@/components/atomic';

interface CreateNewPasswordModalProps {
  onSuccess: () => void;
}

interface PasswordRequirement {
  id: string;
  text: string;
  validator: (password: string) => boolean;
}

const CreateNewPasswordModal: React.FC<CreateNewPasswordModalProps> = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const requirements: PasswordRequirement[] = [
    {
      id: 'length',
      text: 'At least 8 characters',
      validator: (password) => password.length >= 8,
    },
    {
      id: 'uppercase',
      text: 'One uppercase letter',
      validator: (password) => /[A-Z]/.test(password),
    },
    {
      id: 'lowercase',
      text: 'One lowercase letter',
      validator: (password) => /[a-z]/.test(password),
    },
    {
      id: 'number',
      text: 'One number',
      validator: (password) => /\d/.test(password),
    },
    {
      id: 'special',
      text: 'One special character (!@#$%*&*)',
      validator: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword === confirmPassword && isPasswordValid()) {
      onSuccess();
    }
  };

  const isPasswordValid = () => {
    return requirements.every((req) => req.validator(newPassword));
  };

  const isRequirementMet = (requirement: PasswordRequirement) => {
    return requirement.validator(newPassword);
  };

  return (
    <div className="w-full">
      {/* Title */}
      <h2 className="font-rakesly text-xl font-bold text-gray-800 tracking-wide mb-6 text-center">
        CREATE A NEW PASSWORD
      </h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="New Password *"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="••••••••"
          required
          className="w-full py-3"
        />

        <Input
          label="Confirm Password *"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          required
          className="w-full py-3"
        />

        {/* Password Requirements */}
        <div className="space-y-2 mt-4">
          {requirements.map((requirement) => (
            <div key={requirement.id} className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 shrink-0 flex items-center justify-center ${
                isRequirementMet(requirement) ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {isRequirementMet(requirement) ? (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                ) : (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                    <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className={`font-sans ${
                isRequirementMet(requirement) ? 'text-green-600' : 'text-red-600'
              }`}>
                {requirement.text}
              </span>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="pt-6">
          <CustomButton 
            type="submit"
            disabled={!isPasswordValid() || newPassword !== confirmPassword}
            className={`w-full py-2 px-4 rounded-lg font-sans font-medium transition-all duration-200 ${
              isPasswordValid() && newPassword === confirmPassword
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Save
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

export default CreateNewPasswordModal; 